import type { EngineInput, EngineResult, ScheduleRow, Violation } from '@conciair/scheduler-core';
import { supabase, unwrap } from '../../data/supabase';
import type { ScheduleVersionRow } from '../../data/database.types';
import type { RunSummary } from '../../data/queries';
import { engine } from '../../engine/client';
import { fromAssignmentRows } from '../../engine/mapping';
import { fetchSnapshot } from '../../engine/snapshot';
import { diffSchedules, type SeatChange, type SeatKey } from '../schedule/diff';
import { compareSchedules, countGaps, type ScheduleMismatch } from './compare';

/** Spec 6.2 steps 1 and 2: the preconditions, checked afresh when the modal opens. */
export type PublishStep = 'snapshot' | 'hash' | 'rerun' | 'compare' | 'diff' | 'done';

export type PublishCheck =
  | { kind: 'no-draft' }
  | { kind: 'not-ok'; errors: string[] }
  | { kind: 'stale'; draftHash: string; freshHash: string }
  | { kind: 'invalid'; errors: string[] }
  | { kind: 'mismatch'; mismatches: ScheduleMismatch[]; engineVersion: string; draftEngineVersion: string }
  | {
      kind: 'ready';
      input: EngineInput;
      result: EngineResult;
      draftRows: ScheduleRow[];
      publishedRows: ScheduleRow[] | null;
      publishedVersion: ScheduleVersionRow | null;
      diff: Map<SeatKey, SeatChange>;
      gaps: number;
      violations: Violation[];
      engineVersion: string;
    };

async function loadRows(runId: string, ids: { tailByAircraftId: Record<string, string>; codeByPilotId: Record<string, string> }): Promise<ScheduleRow[]> {
  const rows = unwrap(await supabase.from('schedule_assignments').select('*').eq('run_id', runId).limit(5000));
  return fromAssignmentRows(rows, ids);
}

export async function checkPublish(orgId: string, draft: RunSummary | null, currentVersionId: string | null, onStep: (s: PublishStep) => void): Promise<PublishCheck> {
  if (!draft) return { kind: 'no-draft' };
  if (!draft.ok) return { kind: 'not-ok', errors: (draft.errors as string[]) ?? [] };

  onStep('snapshot');
  const { input, hash } = await fetchSnapshot(orgId);

  onStep('hash');
  if (hash !== draft.input_hash) return { kind: 'stale', draftHash: draft.input_hash, freshHash: hash };

  onStep('rerun');
  const [{ result }, version] = await Promise.all([engine.generate(input), engine.version()]);
  if (!result.ok) return { kind: 'invalid', errors: result.errors };

  onStep('compare');
  const [aircraft, pilots] = await Promise.all([
    unwrap(await supabase.from('aircraft').select('id, tail').eq('org_id', orgId)),
    unwrap(await supabase.from('pilots').select('id, code').eq('org_id', orgId)),
  ]);
  const ids = {
    tailByAircraftId: Object.fromEntries(aircraft.map((a) => [a.id, a.tail])),
    codeByPilotId: Object.fromEntries(pilots.map((p) => [p.id, p.code])),
  };
  const draftRows = await loadRows(draft.id, ids);
  const mismatches = compareSchedules(draftRows, result.schedule);
  if (mismatches.length > 0) return { kind: 'mismatch', mismatches, engineVersion: version.engineVersion, draftEngineVersion: draft.engine_version };

  onStep('diff');
  let publishedRows: ScheduleRow[] | null = null;
  let publishedVersion: ScheduleVersionRow | null = null;
  if (currentVersionId) {
    const v = await supabase.from('schedule_versions').select('*').eq('id', currentVersionId).maybeSingle();
    if (v.error) throw new Error(v.error.message);
    if (v.data) {
      publishedVersion = v.data;
      publishedRows = await loadRows(v.data.run_id, ids);
    }
  }
  onStep('done');
  return {
    kind: 'ready',
    input,
    result,
    draftRows,
    publishedRows,
    publishedVersion,
    diff: publishedRows ? diffSchedules(publishedRows, draftRows) : new Map(),
    gaps: countGaps(draftRows),
    violations: (draft.violations as Violation[] | null) ?? [],
    engineVersion: version.engineVersion,
  };
}

/** Spec 6.2 step 4: one server-side transaction. `releasedThrough` null releases the whole window to pilots. */
export async function publishDraft(runId: string, label: string, releasedThrough: string | null): Promise<ScheduleVersionRow> {
  const res = await supabase.rpc('publish_version', { p_run_id: runId, p_label: label.trim(), p_released_through: releasedThrough ?? undefined }); // omitted = the function's null default = whole window
  if (res.error) throw new Error(res.error.message);
  return res.data;
}
